
// src/hooks/useS3Uploader.ts
import { useCallback, useState } from "react";
import axios, { AxiosProgressEvent } from "axios";
import api from "../api/axiosConfig";

const API_BASE = api.S3config;

/** Presign response */
export interface PresignResponse {
  uploadUrl: string;
  viewUrl: string; // used ONLY for preview
  key: string;
  bucket: string;
  region: string;
}

/** Upload return */
export interface UploadedImageInfo {
  key: string;              // ✅ PRIMARY VALUE
  previewUrl?: string;      // ✅ UI-only
  originalFilename?: string;
  viewUrl?: string;
}

export function useS3Uploader(emrId?: string) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const uploadCore = useCallback(
    async (
      file: File,
      moduleName: string,
      onProgress?: (p: number) => void
    ): Promise<UploadedImageInfo | null> => {
      if (!file) return null;

      setUploading(true);
      setProgress(0);

      try {
        // 1️⃣ Presign
        const presignRes = await api.S3config.post<PresignResponse>(
          "/presign",
          {
            id: emrId,
            moduleName,
            originalFilename: file.name,
            contentType: file.type,
            fileSize: file.size,
          }
        );

        const { uploadUrl, viewUrl, key } = presignRes.data;

        // 2️⃣ Upload
        await axios.put(uploadUrl, file, {
          headers: { "Content-Type": file.type },
          onUploadProgress: (e: AxiosProgressEvent) => {
            const percent = e.total
              ? Math.round((e.loaded * 100) / e.total)
              : 0;
            setProgress(percent);
            onProgress?.(percent);
          },
        });

        setProgress(100);

        // ✅ RETURN KEY (NOT URL)
        return {
          key,
          previewUrl: viewUrl, // optional UI preview
          originalFilename: file.name,
        };
     
      } catch (err: any) {
      console.error("❌ FULL PRESIGN ERROR =>", err?.response?.data);
      console.error("❌ Upload failed", err);
      return null;
    }
      finally {
        setUploading(false);
      }
    },
    [emrId]
  );

  return {
    uploadImage: uploadCore,
    uploadAudio: uploadCore,
    uploadPdf: uploadCore,
    uploading,
    progress,
  };
}
